import { useTranslation } from 'react-i18next';
import { languages } from '@/i18n';
import { Globe } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LanguageSelector({ variant = 'default' }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = async (code) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
    // Sync language preference to backend if logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await axios.patch(`${API}/user/language`, { language: code }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        // Silent fail - language still works locally
      }
    }
  };

  if (variant === 'minimal') {
    return (
      <div className="flex items-center gap-1" ref={dropdownRef}>
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all text-sm ${
              lang.code === i18n.language 
                ? 'bg-void text-white font-medium' 
                : 'text-void/70 hover:text-void hover:bg-white/50'
            }`}
            data-testid={`lang-${lang.code}`}
            title={lang.name}
          >
            <span>{lang.flag}</span>
            <span className="hidden lg:inline">{lang.code.toUpperCase()}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-void/70 hover:text-void transition-colors rounded-xl hover:bg-white/30"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4" />
        <span className="text-sm font-body">{currentLang.flag} {currentLang.name}</span>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 glass-effect rounded-2xl shadow-xl py-2 z-50 border border-white/20">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-white/50 transition-colors ${
                lang.code === i18n.language ? 'bg-lucid/20 text-void font-medium' : 'text-void/80'
              }`}
              data-testid={`lang-${lang.code}`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="font-body">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
